import type { UseFormReturn } from "react-hook-form";
import { priceStateClears } from "../schemas/programPricingClears";

/**
 * KB-4181 — a program's declared price state (`campLocations.variations[].priceState`).
 *
 * `null` (stored as null or absent) is "Priced": the program's price fields apply, and a session with no
 * price derives as before. `"free"` and `"unknown"` say what a missing price MEANS for this program's
 * sessions. A real price on a session still wins over either (functions/src/shared/offer/priceState.ts).
 */
export type ProgramPriceState = "free" | "unknown";

export const PROGRAM_PRICE_STATE_OPTIONS: ReadonlyArray<{ value: "priced" | ProgramPriceState; label: string; help: string }> = [
  { value: "priced", label: "Priced", help: "Sessions use the prices below." },
  {
    value: "free",
    label: "Free",
    help: "Sessions with no price of their own are shown as Free. A price set on a session still wins.",
  },
  {
    value: "unknown",
    label: "Price unknown",
    help: 'The camp publishes no price. Sessions with no price of their own show "Contact camp for pricing" and never match a price filter.',
  },
];

export function programPriceStateOf(value: unknown): ProgramPriceState | null {
  return value === "free" || value === "unknown" ? value : null;
}

/** "Free" / "Price unknown" for a declared state, null when the program is priced. */
export function programPriceStateLabel(value: unknown): string | null {
  const state = programPriceStateOf(value);
  return state ? PROGRAM_PRICE_STATE_OPTIONS.find((o) => o.value === state)?.label ?? null : null;
}

/**
 * Set the program's price state on the form. Free and Price unknown CLEAR every price field the program
 * carries — the base price, both tiers, the tier declaration and the per-length alternatives — because a
 * stored price would outrank the declaration and the sessions would come out priced anyway.
 */
export function applyProgramPriceState(form: UseFormReturn<any>, next: ProgramPriceState | null): void {
  const opts = { shouldDirty: true };
  form.setValue("priceState", next, opts);
  // KB-4281: the clears are shared with the operator CLI (programPricingClears.ts).
  for (const [field, value] of Object.entries(priceStateClears(next).fields)) form.setValue(field, value, opts);
}
